import React, { useState } from 'react';
import { scrollToTop } from '../../utils/scrollUtils';
import { Container, Typography, Box, Paper, List, ListItem, ListItemIcon, ListItemText, Button, Divider, Grid, Card, CardContent, CardMedia } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowRight from '@mui/icons-material/ArrowRight';
import HomeIcon from '@mui/icons-material/Home';
import WarningIcon from '@mui/icons-material/Warning';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import DynamicInfographic from '../../components/infographics/DynamicInfographic';
import FallRiskInfographics from '../../components/infographics/FallRiskInfographics';
import ModuleSidebar from '../../components/navigation/ModuleSidebar';

const RiskAssessment = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('2.1');
  const [quizAnswers, setQuizAnswers] = useState({});
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showExplanations, setShowExplanations] = useState({});
  const [answerStatus, setAnswerStatus] = useState({});
  
  const handleBack = () => {
    navigate('/courses/fall-prevention');
    scrollToTop();
  };

  // Function to navigate to the next section
  const handleNextSection = () => {
    const currentSectionIndex = sections.findIndex(section => section.id === activeSection);
    if (currentSectionIndex < sections.length - 1) {
      const nextSection = sections[currentSectionIndex + 1].id;
      setActiveSection(nextSection);
      scrollToTop();
    }
  };
  
  // Define correct answers for each question
  const correctAnswers = {
    '2.1-1': 2, // Family income level (NOT typically included)
    '2.1-2': 1, // Balance and gait evaluation
    '2.1-3': 1, // To develop a tailored prevention plan
    '2.2-1': 1, // Grab bars near toilet and shower
    '2.2-2': 1, // Night lights in hallways and bathroom
    '2.2-3': 0, // Uneven walkways and steps
    '2.3-1': 1, // Handrails on both sides
    '2.3-2': 1, // 32 inches
    '2.3-3': 2, // Move walker forward first, then step into it
    '2.4-1': 2, // Standard walker
    '2.4-2': 1, // Level with the wrist when arm is hanging straight
    '2.4-3': 0, // On the stronger side
    '2.5-1': 0, // Timed Up and Go (TUG) Test
    '2.5-2': 1, // 14 seconds or more
    '2.5-3': 2, // Parkinson's Disease
  };
  
  const handleAnswerSelect = (sectionId, questionId, answerIndex) => {
    const key = `${sectionId}-${questionId}`;
    
    // Check if the correct answer has already been selected for this question
    // If so, don't allow further selections
    if (answerStatus[key] === true) {
      return;
    }
    
    // Store the selected answer
    setSelectedAnswers(prev => ({
      ...prev,
      [key]: answerIndex
    }));
    
    // Check if the selected answer is correct
    const isCorrect = correctAnswers[key] === answerIndex;
    
    // Only show explanation if the correct answer is selected
    if (isCorrect) {
      setShowExplanations(prev => ({
        ...prev,
        [key]: true
      }));
    } else {
      // If incorrect, ensure explanation is hidden
      setShowExplanations(prev => ({
        ...prev,
        [key]: false
      }));
    }
    
    // Track if answer is correct or incorrect
    setAnswerStatus(prev => ({
      ...prev,
      [key]: isCorrect
    }));
  };
  
  // Function to handle section changes
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    scrollToTop();
  };
  
  // Define all sections for the module
  const sections = [
    {
      id: '2.1',
      title: '2.1 Conducting a Fall Risk Assessment',
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <AssignmentIcon sx={{ mr: 1, color: '#64b5f6' }} /> 2.1 Conducting a Fall Risk Assessment
          </Typography>
          
          <Typography paragraph>
            A comprehensive fall risk assessment is the foundation of effective fall prevention. 
            It helps identify specific risk factors and guides the development of personalized prevention strategies.
          </Typography>
          
          {/* Dynamic Infographic - Switch between pre-designed and AI-generated */}
          <DynamicInfographic module="RiskAssessment" topic="Fall Risk Assessment Process" />

          <Box sx={{ bgcolor: 'rgba(100, 181, 246, 0.1)', p: 2, borderRadius: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Assessment Process Guide</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'white' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: '#64b5f6', fontWeight: 600 }}>
                      Step 1: Medical History Review
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#64b5f6' }} /></ListItemIcon>
                        <ListItemText primary="Previous falls (frequency, circumstances, injuries)" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#64b5f6' }} /></ListItemIcon>
                        <ListItemText primary="Chronic conditions (arthritis, Parkinson's, etc.)" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#64b5f6' }} /></ListItemIcon>
                        <ListItemText primary="Current medications and potential side effects" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'white' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: '#64b5f6', fontWeight: 600 }}>
                      Step 2: Physical Assessment
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#64b5f6' }} /></ListItemIcon>
                        <ListItemText primary="Balance and gait evaluation" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#64b5f6' }} /></ListItemIcon>
                        <ListItemText primary="Muscle strength and flexibility" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#64b5f6' }} /></ListItemIcon>
                        <ListItemText primary="Vision and hearing assessment" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'white' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: '#64b5f6', fontWeight: 600 }}>
                      Step 3: Environmental Assessment
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#64b5f6' }} /></ListItemIcon>
                        <ListItemText primary="Home safety evaluation" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#64b5f6' }} /></ListItemIcon>
                        <ListItemText primary="Identification of hazards and obstacles" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#64b5f6' }} /></ListItemIcon>
                        <ListItemText primary="Lighting and accessibility assessment" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'white' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: '#64b5f6', fontWeight: 600 }}>
                      Step 4: Risk Stratification
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#64b5f6' }} /></ListItemIcon>
                        <ListItemText primary="Categorize as low, moderate, or high risk" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#64b5f6' }} /></ListItemIcon>
                        <ListItemText primary="Develop tailored prevention plan" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#64b5f6' }} /></ListItemIcon>
                        <ListItemText primary="Set timeline for reassessment" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
          
          {/* Quiz for Section 2.1 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#64b5f6', fontWeight: 600 }}>
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which of the following is NOT typically included in a medical history review for fall risk assessment?
                    </Typography>
                    <List dense>
                      {['Previous falls history', 'Current medications', 'Family income level'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['2.1-1'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['2.1-1']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['2.1-1'] && index === correctAnswers['2.1-1']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('2.1', '1', index)}
                            disabled={answerStatus['2.1-1'] === true && index !== correctAnswers['2.1-1']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['2.1-1'] === true && index !== correctAnswers['2.1-1']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['2.1-1'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(100, 181, 246, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          While previous falls history and current medications are essential components of a fall risk assessment, family income level is not typically included as it is not directly relevant to physical fall risk factors.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which assessment is most important for evaluating a person's mobility and fall risk?
                    </Typography>
                    <List dense>
                      {['Blood pressure measurement', 'Balance and gait evaluation', 'Cognitive assessment'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['2.1-2'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['2.1-2']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['2.1-2'] && index === correctAnswers['2.1-2']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('2.1', '2', index)}
                            disabled={answerStatus['2.1-2'] === true && index !== correctAnswers['2.1-2']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['2.1-2'] === true && index !== correctAnswers['2.1-2']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['2.1-2'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(100, 181, 246, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Balance and gait evaluation directly assesses how a person moves and maintains stability, which are critical factors in determining fall risk. This assessment can identify specific mobility issues that need to be addressed in a prevention plan.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is the primary purpose of risk stratification in fall assessment?
                    </Typography>
                    <List dense>
                      {['To determine insurance coverage', 'To develop a tailored prevention plan', 'To assign blame for previous falls'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['2.1-3'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['2.1-3']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['2.1-3'] && index === correctAnswers['2.1-3']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('2.1', '3', index)}
                            disabled={answerStatus['2.1-3'] === true && index !== correctAnswers['2.1-3']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['2.1-3'] === true && index !== correctAnswers['2.1-3']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['2.1-3'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(100, 181, 246, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Risk stratification (categorizing as low, moderate, or high risk) allows healthcare providers to develop tailored prevention plans that address the specific risk factors identified during assessment, ensuring resources are appropriately allocated.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )
    },
    {
      id: '2.2',
      title: '2.2 Identifying and Eliminating Environmental Hazards',
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 1, color: '#64b5f6' }} /> 2.2 Identifying and Eliminating Environmental Hazards
          </Typography>
          
          <Typography paragraph>
            Environmental hazards are a leading cause of falls. Identifying and eliminating these hazards 
            is a crucial step in creating a safer living environment.
          </Typography>
          
          {/* Home Safety Infographic */}
          <Box sx={{ mb: 3 }}>
            <FallRiskInfographics 
              type="riskFactorBreakdown" 
              title="Home Safety Assessment"
              description="Comprehensive breakdown of home safety factors and their relative contribution to fall risk prevention."
            />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#64b5f6', fontWeight: 600 }}>Home Safety Modifications</Typography>
                  <Typography variant="body2" paragraph>
                    Making simple modifications to the home environment can significantly reduce fall risk. Key areas to address include:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#e57373' }}>
                        Bathroom Safety:
                      </Typography>
                      <List dense disablePadding>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#e57373', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Install grab bars in shower/tub and near toilet" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#e57373', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Use non-slip mats in shower/tub" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#e57373', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Consider a shower chair and handheld shower" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#e57373', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Ensure adequate lighting" />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#64b5f6' }}>
                        Lighting & Visibility:
                      </Typography>
                      <List dense disablePadding>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#64b5f6', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Install bright, non-glare lighting" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#64b5f6', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Use night lights in hallways and bathroom" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#64b5f6', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Ensure light switches are accessible" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#64b5f6', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Mark edges of steps with bright tape" />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#4db6ac' }}>
                        Trip Hazard Removal:
                      </Typography>
                      <List dense disablePadding>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#4db6ac', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Remove or secure loose rugs" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#4db6ac', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Clear pathways of clutter" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#4db6ac', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Secure electrical cords against walls" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#4db6ac', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Repair uneven flooring" />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Common Indoor Hazards</Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><WarningIcon sx={{ color: '#64b5f6' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Loose rugs and mats" 
                        secondary="Remove or secure with non-slip backing"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><WarningIcon sx={{ color: '#64b5f6' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Cluttered walkways" 
                        secondary="Keep pathways clear of obstacles"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><WarningIcon sx={{ color: '#64b5f6' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Poor lighting" 
                        secondary="Ensure adequate lighting, especially in stairways"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><WarningIcon sx={{ color: '#64b5f6' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Electrical cords across walkways" 
                        secondary="Secure cords along walls or under furniture"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><WarningIcon sx={{ color: '#64b5f6' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Slippery floors" 
                        secondary="Use non-slip mats in bathrooms and kitchens"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Common Outdoor Hazards</Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><WarningIcon sx={{ color: '#64b5f6' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Uneven walkways" 
                        secondary="Repair cracks and level uneven surfaces"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><WarningIcon sx={{ color: '#64b5f6' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Poor outdoor lighting" 
                        secondary="Install motion-sensor lights for pathways"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><WarningIcon sx={{ color: '#64b5f6' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Slippery surfaces when wet" 
                        secondary="Apply anti-slip treatments to walkways"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><WarningIcon sx={{ color: '#64b5f6' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Loose handrails" 
                        secondary="Secure and repair all outdoor handrails"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><WarningIcon sx={{ color: '#64b5f6' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Seasonal hazards (ice, leaves)" 
                        secondary="Clear walkways promptly of snow, ice, and wet leaves"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {/* Quiz for Section 2.2 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#64b5f6', fontWeight: 600 }}>
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which bathroom modification is most effective for preventing falls?
                    </Typography>
                    <List dense>
                      {['Thick bath mats', 'Grab bars near toilet and shower', 'Bright decorative tiles'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['2.2-1'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['2.2-1']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['2.2-1'] && index === correctAnswers['2.2-1']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('2.2', '1', index)}
                            disabled={answerStatus['2.2-1'] === true && index !== correctAnswers['2.2-1']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['2.2-1'] === true && index !== correctAnswers['2.2-1']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['2.2-1'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(100, 181, 246, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Grab bars near toilets and showers provide crucial support for balance during transfers and bathing activities. They offer a secure handhold that can prevent falls in wet, slippery environments where falls commonly occur.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which lighting modification best reduces fall risk at night?
                    </Typography>
                    <List dense>
                      {['Bright overhead lights', 'Night lights in hallways and bathroom', 'Keeping all lights off to avoid disrupting sleep'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['2.2-2'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['2.2-2']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['2.2-2'] && index === correctAnswers['2.2-2']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('2.2', '2', index)}
                            disabled={answerStatus['2.2-2'] === true && index !== correctAnswers['2.2-2']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['2.2-2'] === true && index !== correctAnswers['2.2-2']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['2.2-2'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(100, 181, 246, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Night lights in hallways and bathrooms provide sufficient illumination for safe navigation without being disruptive to sleep. They help individuals see potential obstacles and safely navigate to the bathroom during nighttime hours when falls are common.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which is the most important outdoor hazard to address for fall prevention?
                    </Typography>
                    <List dense>
                      {['Uneven walkways and steps', 'Garden aesthetics', 'Fence height'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['2.2-3'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['2.2-3']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['2.2-3'] && index === correctAnswers['2.2-3']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('2.2', '3', index)}
                            disabled={answerStatus['2.2-3'] === true && index !== correctAnswers['2.2-3']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['2.2-3'] === true && index !== correctAnswers['2.2-3']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['2.2-3'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(100, 181, 246, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Uneven walkways and steps are significant outdoor fall hazards. Repairing cracks, leveling uneven surfaces, and ensuring steps are uniform in height with visible edges can substantially reduce the risk of outdoor falls.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )
    },
    {
      id: '2.3',
      title: '2.3 Safe Home and Facility Modifications',
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 1, color: '#64b5f6' }} /> 2.3 Safe Home and Facility Modifications
          </Typography>
          
          <Typography paragraph>
            Strategic modifications to the home or facility environment can significantly reduce fall risk and enhance safety.
          </Typography>
          
          {/* Home Modification Infographic */}
          <Box sx={{ mb: 3 }}>
            <FallRiskInfographics 
              type="preventionEffectiveness" 
              title="Home Modifications for Fall Prevention"
              description="Evidence-based home modifications and their effectiveness in reducing fall risk for older adults."
            />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#64b5f6', fontWeight: 600 }}>Structural Modifications</Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#64b5f6' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Install handrails on both sides of stairs" 
                        secondary="Ensure they extend the full length of staircase"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#64b5f6' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Add ramps for entryways with steps" 
                        secondary="Slope should not exceed 1:12 ratio for safety"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#64b5f6' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Widen doorways for walker/wheelchair access" 
                        secondary="Minimum 32 inches clearance recommended"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#64b5f6' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Install lever-style door handles" 
                        secondary="Easier to operate than round knobs"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#64b5f6', fontWeight: 600 }}>Furniture and Layout Adjustments</Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#64b5f6' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Arrange furniture to create clear pathways" 
                        secondary="Minimum 36 inches wide for safe navigation"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#64b5f6' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Secure or remove unstable furniture" 
                        secondary="Avoid items that might tip if leaned on"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#64b5f6' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Adjust bed and chair heights" 
                        secondary="Seat height should allow feet flat on floor"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#64b5f6' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Place frequently used items within easy reach" 
                        secondary="Avoid need for reaching high or bending low"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {/* Quiz for Section 2.3 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#64b5f6', fontWeight: 600 }}>
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which structural modification is most important for stairway safety?
                    </Typography>
                    <List dense>
                      {['Carpeted stairs', 'Handrails on both sides', 'Decorative stair lighting'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['2.3-1'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['2.3-1']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['2.3-1'] && index === correctAnswers['2.3-1']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('2.3', '1', index)}
                            disabled={answerStatus['2.3-1'] === true && index !== correctAnswers['2.3-1']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['2.3-1'] === true && index !== correctAnswers['2.3-1']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['2.3-1'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(100, 181, 246, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Handrails on both sides of stairs provide crucial support for balance when ascending or descending. They should extend the full length of the staircase to ensure continuous support throughout the entire journey up or down the stairs.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is the recommended minimum width for doorways to accommodate walkers and wheelchairs?
                    </Typography>
                    <List dense>
                      {['24 inches', '32 inches', '48 inches'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['2.3-2'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['2.3-2']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['2.3-2'] && index === correctAnswers['2.3-2']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('2.3', '2', index)}
                            disabled={answerStatus['2.3-2'] === true && index !== correctAnswers['2.3-2']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['2.3-2'] === true && index !== correctAnswers['2.3-2']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['2.3-2'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(100, 181, 246, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          A minimum width of 32 inches is recommended for doorways to accommodate walkers and wheelchairs. This width provides sufficient clearance for most mobility devices while still being a practical modification for most homes.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is the proper technique when using a walker?
                    </Typography>
                    <List dense>
                      {['Move the walker after stepping forward', 'Lean heavily on the walker for support', 'Move walker forward first, then step into it'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['2.3-3'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['2.3-3']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['2.3-3'] && index === correctAnswers['2.3-3']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('2.3', '3', index)}
                            disabled={answerStatus['2.3-3'] === true && index !== correctAnswers['2.3-3']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['2.3-3'] === true && index !== correctAnswers['2.3-3']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['2.3-3'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(100, 181, 246, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          The proper technique is to move the walker forward first, then step into it. This ensures the walker is stable and positioned correctly before weight is transferred, reducing the risk of falls from improper assistive device use.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )
    },
    {
      id: '2.4',
      title: '2.4 Proper Use of Assistive Devices',
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <AccessibilityIcon sx={{ mr: 1, color: '#64b5f6' }} /> 2.4 Proper Use of Assistive Devices
          </Typography>
          
          <Typography paragraph>
            Assistive devices can provide stability and support, but must be properly fitted and used correctly to be effective.
          </Typography>
          
          {/* Assistive Devices Grid */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#64b5f6', fontWeight: 600 }}>Types of Walking Aids</Typography>
                  <Typography variant="body2" paragraph>
                    Different walking aids provide different levels of support. Selection should be based on individual needs and abilities.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#e57373' }}>
                        Canes:
                      </Typography>
                      <List dense disablePadding>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#e57373', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Standard (single-point)" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#e57373', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Quad cane (four-point base)" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#e57373', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Offset handle cane" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#e57373', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Best for: Mild balance issues" />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#64b5f6' }}>
                        Walkers:
                      </Typography>
                      <List dense disablePadding>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#64b5f6', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Standard walker" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#64b5f6', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Front-wheeled walker" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#64b5f6', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Four-wheeled walker (rollator)" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#64b5f6', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Best for: Moderate stability needs" />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#64b5f6', fontWeight: 600 }}>Proper Walker Technique</Typography>
                  <Typography variant="body2" paragraph>
                    Using the correct technique with assistive devices is crucial for safety and effectiveness. Improper use can increase fall risk.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#e57373' }}>
                        Proper Walker Positioning:
                      </Typography>
                      <List dense disablePadding>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#e57373', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Stand upright with walker in front" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#e57373', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Keep walker approximately arm's length away" />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#64b5f6' }}>
                        Walking Sequence:
                      </Typography>
                      <List dense disablePadding>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#64b5f6', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Move walker forward first" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#64b5f6', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Push down through handgrips for stability" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#64b5f6', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Step forward with weaker leg first" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#64b5f6', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Then step with stronger leg" />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {/* Quiz for Section 2.4 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#64b5f6', fontWeight: 600 }}>
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which assistive device provides the most stability for someone with significant balance issues?
                    </Typography>
                    <List dense>
                      {['Quad cane', 'Standard walker', 'Rolling walker'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['2.4-1'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['2.4-1']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['2.4-1'] && index === correctAnswers['2.4-1']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('2.4', '1', index)}
                            disabled={answerStatus['2.4-1'] === true && index !== correctAnswers['2.4-1']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['2.4-1'] === true && index !== correctAnswers['2.4-1']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['2.4-1'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(100, 181, 246, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          A standard walker (without wheels) provides the most stability as it requires the user to lift and place it with each step. This makes it ideal for those with significant balance issues who need maximum support, though it requires more upper body strength to use properly.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is the proper height for a cane or walker?
                    </Typography>
                    <List dense>
                      {['Chest height', 'Level with the wrist when arm is hanging straight', 'Waist height'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['2.4-2'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['2.4-2']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['2.4-2'] && index === correctAnswers['2.4-2']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('2.4', '2', index)}
                            disabled={answerStatus['2.4-2'] === true && index !== correctAnswers['2.4-2']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['2.4-2'] === true && index !== correctAnswers['2.4-2']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['2.4-2'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(100, 181, 246, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          The proper height for a cane or walker is level with the wrist when the arm is hanging straight at the side. This ensures a slight bend in the elbow (about 15-30 degrees) when using the device, which provides optimal support and reduces strain.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      On which side should a cane be held when one leg is weaker than the other?
                    </Typography>
                    <List dense>
                      {['On the stronger side', 'On the weaker side', 'It doesn\'t matter which side'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['2.4-3'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['2.4-3']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['2.4-3'] && index === correctAnswers['2.4-3']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('2.4', '3', index)}
                            disabled={answerStatus['2.4-3'] === true && index !== correctAnswers['2.4-3']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['2.4-3'] === true && index !== correctAnswers['2.4-3']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['2.4-3'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(100, 181, 246, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          A cane should be held on the stronger side, opposite to the weaker leg. This provides better balance and support as the cane and weaker leg can move forward together, followed by the stronger leg, creating a more stable walking pattern.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )
    },
    {
      id: '2.5',
      title: '2.5 Standardized Assessment Tests',
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <AssessmentIcon sx={{ mr: 1, color: '#ffb74d' }} /> 2.5 Standardized Assessment Tests
          </Typography>
          
          <Typography paragraph>
            Standardized assessment tests provide objective measures of fall risk and functional mobility. These tests help caregivers identify specific areas of concern and track progress over time.
          </Typography>
          
          {/* Assessment Tests Grid */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(255, 183, 77, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#ffb74d', fontWeight: 600 }}>Timed Up and Go (TUG) Test</Typography>
                  <Typography variant="body2" paragraph>
                    The TUG test measures basic mobility and balance. It's quick to administer and requires minimal equipment.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#e57373' }}>
                        Test Procedure:
                      </Typography>
                      <List dense disablePadding>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#e57373', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Patient sits in standard armchair" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#e57373', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="On 'Go,' stand up and walk 3 meters" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#e57373', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Turn around and return to chair" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#e57373', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Sit down again" />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#64b5f6' }}>
                        Interpretation:
                      </Typography>
                      <List dense disablePadding>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#64b5f6', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="<10 seconds: Normal mobility" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#64b5f6', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="11-20 seconds: Good mobility" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#64b5f6', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="21-29 seconds: Moderate impairment" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#64b5f6', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="≥30 seconds: Severe impairment" />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(255, 183, 77, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#ffb74d', fontWeight: 600 }}>Other Assessment Tools</Typography>
                  <Typography variant="body2" paragraph>
                    Multiple assessment tools should be used for a comprehensive evaluation of fall risk factors.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#4db6ac' }}>
                        Balance Tests:
                      </Typography>
                      <List dense disablePadding>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#4db6ac', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Berg Balance Scale (BBS)" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#4db6ac', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Single Leg Stance Test" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#4db6ac', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Functional Reach Test" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#4db6ac', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Tinetti Balance Assessment" />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#9575cd' }}>
                        Condition-Specific Tests:
                      </Typography>
                      <List dense disablePadding>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#9575cd', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Parkinson's: Pull Test" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#9575cd', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Stroke: Fugl-Meyer Assessment" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#9575cd', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Dementia: STRATIFY Risk Assessment" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#9575cd', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Diabetes: Foot Sensation Testing" />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {/* Quiz for Section 2.5 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#64b5f6', fontWeight: 600 }}>
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which test is most commonly used to assess fall risk in older adults?
                    </Typography>
                    <List dense>
                      {['Timed Up and Go (TUG) Test', 'Blood Pressure Test', 'Cognitive Assessment Test'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['2.5-1'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['2.5-1']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['2.5-1'] && index === correctAnswers['2.5-1']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('2.5', '1', index)}
                            disabled={answerStatus['2.5-1'] === true && index !== correctAnswers['2.5-1']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['2.5-1'] === true && index !== correctAnswers['2.5-1']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['2.5-1'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(100, 181, 246, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          The Timed Up and Go (TUG) Test is widely used to assess fall risk in older adults. It measures the time it takes for a person to rise from a chair, walk 3 meters, turn around, walk back to the chair, and sit down again.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      In the TUG test, what time indicates a high fall risk for older adults?
                    </Typography>
                    <List dense>
                      {['Less than 10 seconds', '14 seconds or more', 'Exactly 12 seconds'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['2.5-2'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['2.5-2']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['2.5-2'] && index === correctAnswers['2.5-2']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('2.5', '2', index)}
                            disabled={answerStatus['2.5-2'] === true && index !== correctAnswers['2.5-2']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['2.5-2'] === true && index !== correctAnswers['2.5-2']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['2.5-2'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(100, 181, 246, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          A TUG test time of 14 seconds or more indicates a high risk of falls in older adults. Times between 11-13 seconds suggest moderate risk, while less than 10 seconds generally indicates normal mobility with low fall risk.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which condition requires a specialized fall risk assessment called the Pull Test?
                    </Typography>
                    <List dense>
                      {['Diabetes', 'Stroke', 'Parkinson\'s Disease'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['2.5-3'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['2.5-3']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['2.5-3'] && index === correctAnswers['2.5-3']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('2.5', '3', index)}
                            disabled={answerStatus['2.5-3'] === true && index !== correctAnswers['2.5-3']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['2.5-3'] === true && index !== correctAnswers['2.5-3']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['2.5-3'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(100, 181, 246, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          The Pull Test is specifically used to assess postural stability in patients with Parkinson's Disease. This test evaluates how well a person can maintain balance when pulled backward slightly, which is particularly important for Parkinson's patients who often experience postural instability.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )
    }
  ];
  
  // Find the active section content
  const activeSectionContent = sections.find(section => section.id === activeSection)?.content;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button 
        variant="outlined" 
        startIcon={<ArrowBackIcon />} 
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        Back to Learning Modules
      </Button>

      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ color: '#64b5f6', fontWeight: 600 }}>
          Module 2: Risk Assessment and Prevention Strategies
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 3, color: 'text.secondary' }}>
          This module covers conducting fall risk assessments, identifying and eliminating environmental hazards, 
          implementing safe home modifications, and properly using assistive devices.
        </Typography>

        <Divider sx={{ my: 3 }} />
        
        {/* Module Content with Sidebar */}
        <Grid container spacing={3}>
          {/* Sidebar */}
          <Grid item xs={12} md={3}>
            <ModuleSidebar 
              sections={sections} 
              activeSection={activeSection} 
              onSectionChange={handleSectionChange} 
              moduleTitle="Risk Assessment"
            />
          </Grid>
          
          {/* Main Content */}
          <Grid item xs={12} md={9}>
            {activeSectionContent}
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />} 
            onClick={handleBack}
          >
            Back to Learning Modules
          </Button>
          {activeSection !== sections[sections.length - 1].id && (
            <Button 
              variant="contained" 
              endIcon={<ArrowForwardIcon />} 
              onClick={handleNextSection}
              sx={{ bgcolor: '#64b5f6', '&:hover': { bgcolor: '#2196f3' } }}
            >
              Next Section
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default RiskAssessment;
